import { useMemo } from 'react';
import { useWorkflowMetadata, WorkflowStatus } from './useWorkflowMetadata';

/**
 * Status Transitions Manager
 *
 * This hook provides business logic and validation for status transitions within a workflow.
 * It builds on top of useWorkflowMetadata to provide convenient methods for:
 * - Determining allowed status transitions
 * - Validating transition data (reasons, notes)
 * - Getting required fields for a status change
 *
 * **Architecture:**
 * - Wraps useWorkflowMetadata to provide derived state and validation methods
 * - Presentation-agnostic: works with buttons, dropdowns, or custom UI
 * - Enforces business rules defined in the backend workflow configuration
 *
 * **Usage Pattern:**
 * ```typescript
 * const {
 *   availableTransitions,   // Statuses user can transition to
 *   canTransitionTo,        // Check if specific transition is allowed
 *   getRequiredFields,      // Get required fields for a status
 *   validateTransition,     // Validate transition data before submission
 * } = useStatusTransitions({
 *   currentStatus: 'NEW',
 *   workflowScope: 'staffing.need',
 * });
 *
 * // Render buttons for available transitions
 * availableTransitions.map(status => (
 *   <button onClick={() => handleTransition(status.code)}>
 *     {status.verb}
 *   </button>
 * ));
 *
 * // Check if transition needs additional fields
 * const required = getRequiredFields('CLOSED');
 * if (required.reason) {
 *   // Show reason dropdown
 * }
 *
 * // Validate before submission
 * const validation = validateTransition('CLOSED', {
 *   reason: 'FILLED',
 *   note: undefined,
 * });
 * if (!validation.isValid) {
 *   alert(validation.errors.join('\n'));
 * }
 * ```
 *
 * **Separation of Concerns:**
 * - This hook: Provides rules and validation logic
 * - Your component: Handles UI rendering and user interaction
 * - Backend: Defines workflow rules and persists status changes
 */

/**
 * Required fields information for a status transition
 */
export interface RequiredFields {
  /** Whether a reason must be selected */
  reason: boolean;
  /** Whether a note must be provided */
  note: boolean;
  /** Available reason options (if reason is required) */
  reasons: Array<{
    code: string;
    title: string;
    require_note: boolean;
  }>;
}

/**
 * Validation result for a status transition
 */
export interface TransitionValidation {
  /** Whether the transition is valid */
  isValid: boolean;
  /** Array of validation error messages */
  errors: string[];
  /** Information about required fields */
  requiredFields: RequiredFields;
}

/**
 * Data provided for a status transition
 */
export interface TransitionData {
  /** Reason code (if applicable) */
  reason?: string;
  /** Additional note/comment */
  note?: string;
}

export interface UseStatusTransitionsParams {
  /** Current status code of the object */
  currentStatus: string | null | undefined;
  /** Workflow scope identifier (e.g., 'assignments.opportunity', 'staffing.need') */
  workflowScope: string;
}

/**
 * Hook for managing status transitions within a workflow.
 *
 * Provides business logic and validation for status changes, including:
 * - Available transitions from current status
 * - Required fields (reasons, notes) for each transition
 * - Validation before submitting status changes
 *
 * @param params - Configuration object
 * @param params.currentStatus - Current status code of the object (can be null/undefined for new objects)
 * @param params.workflowScope - Workflow scope identifier
 * @returns Object containing transition data and validation methods
 *
 * @example
 * ```typescript
 * // Basic usage with buttons
 * function StatusButtons({ opportunity, onStatusChange }) {
 *   const { availableTransitions, getRequiredFields } = useStatusTransitions({
 *     currentStatus: opportunity.status,
 *     workflowScope: 'assignments.opportunity',
 *   });
 *
 *   return availableTransitions.map(status => (
 *     <button
 *       key={status.code}
 *       onClick={() => {
 *         const required = getRequiredFields(status.code);
 *         if (required.reason || required.note) {
 *           // Show modal to collect additional info
 *           showModal(status.code, required);
 *         } else {
 *           // Can submit immediately
 *           onStatusChange({ status: status.code });
 *         }
 *       }}
 *     >
 *       {status.verb}
 *     </button>
 *   ));
 * }
 *
 * // Usage with validation
 * function handleSubmit(targetStatus: string, data: TransitionData) {
 *   const validation = validateTransition(targetStatus, data);
 *   if (!validation.isValid) {
 *     setErrors(validation.errors);
 *     return;
 *   }
 *   onStatusChange({ status: targetStatus, ...data });
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Usage with dropdown
 * function StatusDropdown({ staffingNeed, onChange }) {
 *   const { availableTransitions, currentStatusData } = useStatusTransitions({
 *     currentStatus: staffingNeed.status,
 *     workflowScope: 'staffing.need',
 *   });
 *
 *   return (
 *     <select value={staffingNeed.status} onChange={e => handleChange(e.target.value)}>
 *       <option value={staffingNeed.status}>{currentStatusData?.title}</option>
 *       {availableTransitions.map(status => (
 *         <option key={status.code} value={status.code}>
 *           {status.title}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useStatusTransitions({ currentStatus, workflowScope }: UseStatusTransitionsParams) {
  const { statuses, isLoading, error } = useWorkflowMetadata(workflowScope);

  /**
   * Metadata for the current status
   */
  const currentStatusData = useMemo(
    () => (currentStatus ? statuses[currentStatus] : null),
    [statuses, currentStatus]
  );

  /**
   * Array of status objects that can be transitioned to from the current status.
   * Returns full WorkflowStatus objects, not just codes.
   */
  const availableTransitions = useMemo<WorkflowStatus[]>(() => {
    if (!currentStatusData) return [];

    return (
      currentStatusData.allowed_transitions?.map((code) => statuses[code]).filter(Boolean) || []
    );
  }, [currentStatusData, statuses]);

  /**
   * Check if a specific status transition is allowed from the current status.
   *
   * @param targetStatus - The status code to check
   * @returns true if transition is allowed, false otherwise
   *
   * @example
   * ```typescript
   * if (canTransitionTo('CLOSED')) {
   *   // Show close button
   * }
   * ```
   */
  const canTransitionTo = (targetStatus: string): boolean => {
    return currentStatusData?.allowed_transitions?.includes(targetStatus) || false;
  };

  /**
   * Get required fields information for a specific status transition.
   *
   * Use this to determine what additional data needs to be collected
   * from the user before submitting a status change.
   *
   * @param targetStatus - The status code to get requirements for
   * @returns Object describing required fields and available options
   *
   * @example
   * ```typescript
   * const required = getRequiredFields('CLOSED');
   *
   * // Check what's required
   * if (required.reason) {
   *   // Show reason dropdown with required.reasons options
   * }
   * if (required.note) {
   *   // Show note textarea
   * }
   * ```
   */
  const getRequiredFields = (targetStatus: string): RequiredFields => {
    const targetStatusData = statuses[targetStatus];

    if (!targetStatusData) {
      return {
        reason: false,
        note: false,
        reasons: [],
      };
    }

    return {
      reason: targetStatusData.require_reason || false,
      note: targetStatusData.require_note || false,
      reasons: targetStatusData.reasons || [],
    };
  };

  /**
   * Validate a proposed status transition with the provided data.
   *
   * Checks:
   * - Whether the transition is allowed from the current status
   * - Whether required reason is provided
   * - Whether required note is provided
   * - Whether specific reason requires a note
   *
   * @param targetStatus - The status code to transition to
   * @param data - The data being submitted (reason, note)
   * @returns Validation result with isValid flag and error messages
   *
   * @example
   * ```typescript
   * const handleSubmit = () => {
   *   const validation = validateTransition('CLOSED', {
   *     reason: selectedReason,
   *     note: noteText,
   *   });
   *
   *   if (!validation.isValid) {
   *     setErrors(validation.errors);
   *     return;
   *   }
   *
   *   // Submit to backend
   *   onStatusChange({
   *     status: 'CLOSED',
   *     reason: selectedReason,
   *     note: noteText,
   *   });
   * };
   * ```
   */
  const validateTransition = (targetStatus: string, data: TransitionData): TransitionValidation => {
    const errors: string[] = [];
    const required = getRequiredFields(targetStatus);

    // Check if transition is allowed
    if (!canTransitionTo(targetStatus)) {
      errors.push(`Cannot transition from ${currentStatus || 'current state'} to ${targetStatus}`);
    }

    // Check if reason is required but not provided
    if (required.reason && !data.reason) {
      errors.push('Reason is required for this status change');
    }

    // Check if note is required but not provided
    if (required.note && !data.note?.trim()) {
      errors.push('Note is required for this status change');
    }

    // Check if specific reason requires a note
    if (data.reason && required.reasons.length > 0) {
      const reasonData = required.reasons.find((r) => r.code === data.reason);
      if (reasonData?.require_note && !data.note?.trim()) {
        errors.push(`A note is required when selecting "${reasonData.title}"`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      requiredFields: required,
    };
  };

  return {
    /** All workflow statuses (keyed by status code) */
    statuses,
    /** Metadata for the current status */
    currentStatusData,
    /** Array of statuses that can be transitioned to */
    availableTransitions,
    /** Check if a specific transition is allowed */
    canTransitionTo,
    /** Get required fields for a status transition */
    getRequiredFields,
    /** Validate a transition with provided data */
    validateTransition,
    /** Whether workflow metadata is loading */
    isLoading,
    /** Error from fetching workflow metadata */
    error,
  };
}
